import { DownloadButton } from '@/components/Elements/Button'
import { Checkbox, DatePicker, DummyInput, Input, Select, VersionFileInput } from '@/components/Form'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useUser from '@/features/users/useUser'
import { Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { CostBudgetType } from '@eapproval/offer-shared'
import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

const PRSubmissionGeneralInfo = ({
  costBudgetIndex,
  vendorSelectionIndex,
  submissionIndex,
  canEditPRSubmissionGeneralField,
  canEditSubcontractorAgreementRequired,
  canUploadSubcontractorAgreement,
  submissionDetail,
}) => {
  const { useFetchCostCenter } = useCostCenter()
  const { useUserOptions } = useUser()
  const subSystems = useWatch({ name: 'subSystems' }) ?? []
  const selectedSubSystemName = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subSystem` })
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })

  const { data: allUserOptions } = useUserOptions({
    valueKey: '_id',
    status: 'active',
  })

  const selectedCostCenterId = useMemo(() => {
    if (!Array.isArray(subSystems) || !selectedSubSystemName) {
      return ''
    }

    const selectedSubSystem = subSystems.find((subSystem) => subSystem?.projectName === selectedSubSystemName)

    if (!selectedSubSystem?.profitCenter) {
      return ''
    }

    return selectedSubSystem.profitCenter?._id || selectedSubSystem.profitCenter || ''
  }, [subSystems, selectedSubSystemName])

  const { data: selectedCostCenter } = useFetchCostCenter(selectedCostCenterId || '')
  const workInstructionAllowed =
    (selectedCostCenter?.workInstructionAllowed && costBudgetType !== CostBudgetType.SUPPLY_MATERIAL_ONLY) ?? false

  const hasMissingSAPNumber = useMemo(() => {
    const requestedDetails = Array.isArray(submissionDetail?.requestedDetails) ? submissionDetail.requestedDetails : []

    if (requestedDetails.length === 0) {
      return false
    }

    const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '')

    return requestedDetails.some((requestedItem) => normalizeText(requestedItem?.SAPMaterialNumber) === '')
  }, [submissionDetail?.requestedDetails])

  const submissionPath = `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${submissionIndex}`

  return (
    <>
      <SimpleGrid columns={[2, null, 4]} spacingX="20px" spacingY="10px" py="3">
        <Input
          name={`${submissionPath}.descriptionOfProduct`}
          label="Remarks"
          type="text"
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={false}
          isRow
        />
        <Input
          name={`${submissionPath}.PONumber`}
          label="PO Number"
          type="text"
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={false}
          isRow
        />
        <Checkbox
          name={`${submissionPath}.licenseRequired`}
          label="License Required : (if any) "
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={false}
          isRow
        />
        <DatePicker
          name={`${submissionPath}.expectedDeliveryDate`}
          label="Expected Delivery Date"
          type="text"
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={true}
          isRow
        />
        <Input
          name={`${submissionPath}.deliveryInstructions`}
          label="Delivery Instruction"
          type="text"
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={false}
          isRow
        />
        <VersionFileInput
          name={`${submissionPath}.internalDocument`}
          label="Internal Documents"
          fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
          canUpload={canEditPRSubmissionGeneralField}
          isMultiple
          showVersion={false}
          isRequired={false}
        />
        <Checkbox
          name={`${submissionPath}.subcontractorAgreementRequired`}
          label="Subcontractor Agreement Required"
          disabled={!canEditSubcontractorAgreementRequired}
          isRequired={false}
          isRow
        />
        {submissionDetail?.subcontractorAgreementRequired && (
          <VersionFileInput
            name={`${submissionPath}.subcontractorAgreement`}
            label={'Subcontractor Agreement '}
            fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
            canUpload={canUploadSubcontractorAgreement}
            isMultiple
            showVersion={false}
            isRequired={true}
          />
        )}

        {workInstructionAllowed && (
          <Checkbox
            name={`${submissionPath}.requiredWorkInstructions`}
            label="Required Work Instructions"
            disabled={!canEditPRSubmissionGeneralField}
            isRequired={false}
            isRow
          />
        )}

        {workInstructionAllowed && submissionDetail?.requiredWorkInstructions && (
          <VersionFileInput
            name={`${submissionPath}.requiredWorkInstructionAttachments`}
            label={'Work Instruction Internal Attachment'}
            fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
            canUpload={canEditPRSubmissionGeneralField}
            isMultiple
            showVersion={false}
            isRequired={false}
          />
        )}

        {workInstructionAllowed && submissionDetail?.requiredWorkInstructions && (
          <Input
            name={`${submissionPath}.requiredWorkInstructionRemark`}
            label="Remarks for Reason"
            type="text"
            disabled={!canEditPRSubmissionGeneralField}
            isRequired={false}
            isRow
          />
        )}
        <Input
          name={`${submissionPath}.SAPNumber`}
          label="SO# / SAP PR#"
          type="text"
          disabled={!canEditPRSubmissionGeneralField}
          isRequired={false}
          isRow
        />
      </SimpleGrid>

      {Array.isArray(submissionDetail?.workInstructionFiles) && submissionDetail.workInstructionFiles.length > 0 && (
        <Flex direction="column" gap="2" my="2">
          <Text fontWeight="semibold">Sent Work Instruction Files</Text>
          <Flex gap="2" flexWrap="wrap">
            {submissionDetail.workInstructionFiles.map((file, index) => {
              const fileId = file?._id || file
              const fileName = file?.originalFileName ?? `work_instruction_${index + 1}.pdf`
              return <DownloadButton key={fileId || index} fileId={fileId} fileName={fileName} displayText={fileName} />
            })}
          </Flex>
        </Flex>
      )}
    </>
  )
}

export default PRSubmissionGeneralInfo
